import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      const cartType = window.Theme?.cartType;
      const isNotificationMode =
        cartType === 'notification' ||
        cartType === 'notification_drawer' ||
        document.body?.classList.contains('dcoded-llp-notification-mode');
      const shouldSuppressFlyToCart =
        productForm?.shouldSubmitCheckout || isNotificationMode;

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        if (!shouldSuppressFlyToCart) {
          this.#animateFlyToCart();
        }
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #isAddingToCart = false;

  /** Blocks concurrent /cart/add from this form and from apps (e.g. Kaching). */
  static #globalAddLock = false;

  /** @type {boolean} */
  static #cartAddDedupeInstalled = false;

  /**
   * Global /cart/add dedupe lives in dcoded-llp-cart-add-guard.js (fetch + XHR).
   * Keep a no-op so older call sites stay safe.
   */
  static #installCartAddDedupe() {
    ProductFormComponent.#cartAddDedupeInstalled = true;
  }

  get isQuickAddContext() {
    return Boolean(this.closest('.quick-add__product-form-component, .bh-cart-upsell__form, product-card, .quick-add-modal'));
  }

  /** Cart-drawer upsell ADD — stay in drawer, no LLP toast. */
  get isCartDrawerUpsellContext() {
    return Boolean(this.closest('.bh-cart-upsell__form, .bh-cart-upsell'));
  }

  get postAddAction() {
    return this.dataset.postAddAction || (this.dataset.redirectToCheckout === 'true' ? 'checkout' : 'cart');
  }

  get shouldSubmitCheckout() {
    return this.postAddAction === 'checkout' && !this.isQuickAddContext;
  }

  get shouldOpenCartDrawer() {
    if (this.isCartDrawerUpsellContext) return false;
    if (window.Theme?.cartType === 'notification_drawer') return false;
    return this.postAddAction === 'drawer' && !this.isQuickAddContext;
  }

  get shouldRequestCartNotification() {
    // Upsell is already inside the open drawer — never open the notification toast.
    if (this.isCartDrawerUpsellContext) return false;

    const cartType = window.Theme?.cartType;
    if (this.isQuickAddContext || cartType === 'notification_drawer' || cartType === 'notification') {
      return true;
    }
    return Boolean(document.querySelector('dcoded-llp-cart-notification'));
  }

  connectedCallback() {
    super.connectedCallback();
    ProductFormComponent.#installCartAddDedupe();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    // Upsell is an intentional second add — don't block on the prior ATC's global/busy locks.
    if (this.isCartDrawerUpsellContext) {
      if (this.#isAddingToCart) return;
    } else if (this.#isAddingToCart || ProductFormComponent.#globalAddLock || window.__dcodedLlpAtcBusy) {
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Snapshot for dcoded-llp notification toast (title / image / packs / prices).
   * @returns {{
   *   title: string,
   *   image: string,
   *   sellingPlan: string,
   *   productId: string,
   *   packs: number,
   *   isSubscription: boolean,
   *   months: number,
   *   sale: string,
   *   compare: string,
   *   perPack: string
   * }}
   */
  #getNotificationSnapshot() {
    const card =
      this.closest(
        '.dcoded-llp-start-exploring__card, .product-card, .dcoded-product-card, .product-grid__card, li.product'
      ) || null;

    let title =
      this.dataset.dcodedLlpProductTitle ||
      card?.querySelector(
        '.dcoded-llp-start-exploring__card-title, .product-card-title, .product-title, a[class*="title"]'
      )?.textContent?.trim() ||
      '';
    if (!title) {
      title =
        document.querySelector('.product-title-block .text-block')?.textContent?.trim() ||
        document.querySelector('main .product-title-block')?.textContent?.trim() ||
        '';
    }

    let image =
      this.dataset.dcodedLlpProductImage ||
      card?.querySelector('img')?.currentSrc ||
      card?.querySelector('img')?.src ||
      '';
    if (!image) {
      image =
        document.querySelector(
          'dcoded-llp-media-gallery img, media-gallery img, .product-media-container img'
        )?.currentSrc ||
        document.querySelector('dcoded-llp-media-gallery img, media-gallery img')?.src ||
        '';
    }

    const checked =
      this.querySelector('input[name="selling_plan"]:checked') ||
      document.querySelector('input[name="selling_plan"]:checked');
    const sellingInput = this.querySelector('input[name="selling_plan"]');
    const isSubscription = Boolean(
      (checked instanceof HTMLInputElement && checked.value) ||
        (sellingInput instanceof HTMLInputElement && sellingInput.value)
    );

    let sellingPlan = '';
    if (checked instanceof HTMLInputElement && checked.value) {
      const wrap = checked.closest('label, [class*="kaching"], [class*="selling"], [class*="ks-"]');
      const text = wrap?.textContent?.replace(/\s+/g, ' ').trim() || '';
      const match = text.match(/repeats?\s+every[^.\n]*/i);
      sellingPlan = match ? match[0] : 'Subscription';
    }
    if (!sellingPlan && isSubscription) sellingPlan = 'Subscription';

    const packs = this.#readNotificationPackCount();
    const months = this.#readNotificationMonths(packs);
    const prices = this.#readNotificationPrices();
    let perPack = prices.perPack || '';
    if (!perPack && prices.sale && packs > 1) {
      const n = Number.parseFloat(String(prices.sale).replace(/[^\d.]/g, '')) || 0;
      if (n) {
        try {
          perPack = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n / packs);
        } catch {
          perPack = `£${(n / packs).toFixed(2)}`;
        }
      }
    }

    return {
      title,
      image,
      sellingPlan,
      productId: this.dataset.productId || '',
      packs,
      isSubscription,
      months,
      sale: prices.sale,
      compare: prices.compare,
      perPack,
    };
  }

  /** @returns {number} */
  #readNotificationPackCount() {
    let packs = 0;
    document
      .querySelectorAll('[class*="kaching-bundles__bar"][class*="selected"], .kaching-bundles__bar-container--selected')
      .forEach((el) => {
        if (packs || !(el instanceof HTMLElement)) return;
        const text = (el.textContent || '').replace(/\s+/g, ' ');
        if (/subscribe|one[-\s]?time/i.test(text) && !/buy\s+\d+\s+packs?/i.test(text)) return;
        const match = text.match(/Buy\s+(\d+)\s+Packs?/i);
        if (match) packs = Number.parseInt(match[1], 10) || 0;
      });
    if (packs > 0) return packs;

    const qty = this.querySelector('input[name="quantity"]') || this.refs.quantitySelector;
    const fromSelector = this.refs.quantitySelector?.getValue?.();
    const n = Number(fromSelector) || Number(qty instanceof HTMLInputElement ? qty.value : 0) || this.#getQuantity();
    return n > 0 ? n : 1;
  }

  /** @param {number} packs */
  #readNotificationMonths(packs) {
    const supply = document.querySelector('[data-dcoded-bundle-supply]');
    const supplyMatch = (supply?.textContent || '').replace(/\s+/g, ' ').match(/(\d+)\s*[-–—\s]*Month/i);
    if (supplyMatch) return Number.parseInt(supplyMatch[1], 10) || 0;

    const freq = document.querySelector(
      '[class*="subscriptions_card"][class*="selected"], [class*="ks-card"][class*="selected"]'
    );
    const every = (freq?.textContent || '').replace(/\s+/g, ' ').match(/every\s+(\d+)\s+months?/i);
    if (every) return Number.parseInt(every[1], 10) || 0;

    return packs > 0 ? packs * 3 : 0;
  }

  #readNotificationPrices() {
    /** @type {HTMLElement | null} */
    let card = null;
    document
      .querySelectorAll('[class*="subscriptions_card"], [class*="ks-card"], [class*="onetime"], [class*="one-time"]')
      .forEach((el) => {
        if (card || !(el instanceof HTMLElement)) return;
        if (el.querySelector('input[type="radio"]:checked') || /selected/i.test(String(el.className))) {
          card = el;
        }
      });
    if (!card) {
      document.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        if (card || !(input instanceof HTMLInputElement)) return;
        if (!input.closest('[class*="kaching"], [class*="ks-"]')) return;
        const wrap = input.closest(
          '[class*="subscriptions_card"], [class*="ks-card"], [class*="kaching-bundles__bar-container"]'
        );
        if (wrap instanceof HTMLElement && !/buy\s+\d+\s+packs?/i.test(wrap.textContent || '')) {
          card = wrap;
        }
      });
    }

    const parse = (text) => {
      const match = String(text || '').match(/[£$€]\s*[\d,.]+/);
      return match ? match[0].replace(/\s+/g, '') : '';
    };

    if (!card) {
      const priceEl = document.querySelector(
        '.dcoded-pdp-product-information product-price .price, product-price .price'
      );
      const compareEl = document.querySelector(
        '.dcoded-pdp-product-information product-price .compare-at-price, product-price .compare-at-price'
      );
      return { sale: parse(priceEl?.textContent), compare: parse(compareEl?.textContent), perPack: '' };
    }

    const compareEl = card.querySelector('s, del, [class*="compare"], [class*="original"]');
    const saleEl = card.querySelector(
      '.kaching-bundles__bar-price, .ks-discounted-price, [data-testid="discount-price"], [data-testid="discounted-price"]'
    );
    const all = [...(card.textContent || '').matchAll(/[£$€]\s*[\d,.]+(?!\s*per\s*pack)/gi)].map((m) =>
      m[0].replace(/\s+/g, '')
    );
    const unique = [...new Set(all)];
    let sale = parse(saleEl?.textContent) || '';
    let compare = parse(compareEl?.textContent) || '';
    if (!sale && unique.length) {
      unique.sort((a, b) => {
        const na = Number.parseFloat(a.replace(/[^\d.]/g, '')) || 0;
        const nb = Number.parseFloat(b.replace(/[^\d.]/g, '')) || 0;
        return na - nb;
      });
      sale = unique[0];
      compare = compare || unique[unique.length - 1];
    }
    const saleNum = Number.parseFloat(String(sale).replace(/[^\d.]/g, '')) || 0;
    const compareNum = Number.parseFloat(String(compare).replace(/[^\d.]/g, '')) || 0;
    if (compare && compareNum <= saleNum) compare = '';
    const perMatch = (card.textContent || '').match(/[£$€]\s*[\d,.]+\s*\/?\s*per\s*pack/i);
    return { sale, compare, perPack: perMatch ? parse(perMatch[0]) : '' };
  }

  /**
   * @param {string} text
   * @returns {number}
   */
  #moneyToCents(text) {
    const n = Number.parseFloat(String(text || '').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  /** @returns {{ cents: number, qty: number }} */
  #kachingCompareMeta() {
    const prices = this.#readNotificationPrices();
    const cents = this.#moneyToCents(prices.compare);
    const qty = this.#readNotificationPackCount() || 1;
    return { cents, qty };
  }

  /** @param {FormData} formData */
  #attachKachingCompareProperties(formData) {
    const meta = this.#kachingCompareMeta();
    if (meta.cents <= 0) return;
    formData.set('properties[_kaching_compare]', String(meta.cents));
    formData.set('properties[_kaching_compare_qty]', String(meta.qty));
  }

  /**
   * Kaching often adds without our FormData properties — write compare onto the line after add.
   * @param {Record<string, any> | null | undefined} item
   */
  async #persistKachingCompareOnItem(item) {
    if (!item?.key) return;

    const meta = this.#kachingCompareMeta();
    const live = window.__dcodedKachingCompare;
    const cents = meta.cents || Number(live?.cents) || 0;
    const qty = meta.qty || Number(live?.qty) || Number(item.quantity) || 1;
    if (cents <= 0) return;

    const current = item.properties?._kaching_compare || item.properties?.['_kaching_compare'];
    if (String(current) === String(cents)) return;

    const properties = { ...(item.properties || {}) };
    properties._kaching_compare = String(cents);
    properties._kaching_compare_qty = String(qty);

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties,
          sections: sectionIds.join(','),
        }),
      });
    } catch {
      /* toast / ATC should not fail if compare persist misses */
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #releaseAddLock(allAddToCartContainers) {
    this.#isAddingToCart = false;
    ProductFormComponent.#globalAddLock = false;
    for (const container of allAddToCartContainers) {
      container.enable();
    }
  }

  /**
   * Keep ATC disabled until cart notification is open (or safety timeout).
   * @param {NodeListOf<AddToCartComponent> | AddToCartComponent[]} allAddToCartContainers
   */
  #releaseAddLockWhenNotificationOpens(allAddToCartContainers) {
    let released = false;
    const unlock = () => {
      if (released) return;
      released = true;
      document.removeEventListener('dcoded-llp:notification-opened', unlock);
      window.clearTimeout(safety);
      this.#releaseAddLock(allAddToCartContainers);
    };

    document.addEventListener('dcoded-llp:notification-opened', unlock);
    const safety = window.setTimeout(unlock, 2500);

    // If toast already open (same-session spam), unlock immediately.
    if (
      document.querySelector('#dcoded-llp-cart-notification[data-open="true"]') &&
      window.__dcodedLlpNotificationShouldStayOpen
    ) {
      unlock();
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   * @param {{ upsellRetry?: boolean }} [options]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event, options) {
    const { addToCartTextError } = this.refs;

    // Upsell: only block if THIS form is already posting (not prior PDP/ATC locks).
    if (this.isCartDrawerUpsellContext) {
      if (this.#isAddingToCart) return;
    } else if (this.#isAddingToCart || ProductFormComponent.#globalAddLock || window.__dcodedLlpAtcBusy) {
      return;
    }
    this.#isAddingToCart = true;
    ProductFormComponent.#globalAddLock = true;
    ProductFormComponent.#installCartAddDedupe();
    window.__bhCartUpsell?.lockCheckout?.('atc');

    // Prevent a queued batch add from firing a second POST after this one
    this.#addToCartQueue = [];

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    const form = this.querySelector('form');
    if (!form) {
      this.#releaseAddLock(allAddToCartContainers);
      window.__bhCartUpsell?.unlockCheckout?.('atc');
      throw new Error('Product form element missing');
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          this.#releaseAddLock(allAddToCartContainers);
          window.__bhCartUpsell?.unlockCheckout?.('atc');
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    this.#attachKachingCompareProperties(formData);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const sectionIds = [...cartItemComponentsSectionIds];
    if (this.shouldRequestCartNotification) {
      sectionIds.push('dcoded-llp-cart-notification-product');
    }
    const uniqueSectionIds = [...new Set(sectionIds.filter(Boolean))];

    if (uniqueSectionIds.length > 0) {
      formData.append('sections', uniqueSectionIds.join(','));
    }

    window.__dcodedLlpExpectThemeAdd = Date.now();
    const atcToken =
      typeof window.__dcodedLlpIssueAtcToken === 'function'
        ? window.__dcodedLlpIssueAtcToken(
            this.isCartDrawerUpsellContext ? { bypassCooldown: true } : undefined
          )
        : '';

    if (!atcToken) {
      // Prior ATC still in flight — one automatic retry for upsell after it clears.
      if (this.isCartDrawerUpsellContext && !options?.upsellRetry) {
        this.#releaseAddLock(allAddToCartContainers);
        window.__bhCartUpsell?.unlockCheckout?.('atc');
        window.setTimeout(() => {
          if (this.#isAddingToCart) return;
          this.#processAddToCart(overrideVariantId, overrideQuantity, event, { upsellRetry: true });
        }, 150);
        return;
      }

      // Guard refused — another add is in flight / cooling down
      this.#releaseAddLock(allAddToCartContainers);
      window.__bhCartUpsell?.unlockCheckout?.('atc');
      return;
    }

    // Open our toast once with product snapshot (do not wait on cart:update)
    if (this.shouldRequestCartNotification) {
      document.dispatchEvent(
        new CustomEvent('dcoded-llp:atc-start', {
          bubbles: true,
          detail: this.#getNotificationSnapshot(),
        })
      );
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    let addFailed = false;

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
        'X-Dcoded-Llp-Atc': '1',
        'X-Dcoded-Llp-Atc-Token': atcToken,
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          addFailed = true;
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          if (this.isCartDrawerUpsellContext) {
            // Don't block drawer morph on a second /cart.js round-trip
            void this.#fetchAndUpdateCartQuantity();
          } else {
            await this.#fetchAndUpdateCartQuantity();
          }

          if (this.shouldSubmitCheckout) {
            this.#submitCheckout();
            return;
          }

          // /cart/add.js returns the line item — use it so the notification can paint without sections
          const addedItem = Array.isArray(response.items) ? response.items[0] : response;

          if (this.isCartDrawerUpsellContext) {
            void this.#persistKachingCompareOnItem(addedItem);
          } else {
            await this.#persistKachingCompareOnItem(addedItem);
          }

          this.dispatchEvent(
            new CartAddEvent(addedItem || {}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              cartItemKey: addedItem?.key || response.key,
              redirectToCheckout: false,
              openCartDrawer: this.shouldOpenCartDrawer,
              skipCartNotification: this.isCartDrawerUpsellContext,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        addFailed = true;
        if (error?.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        if (this.shouldRequestCartNotification && !addFailed) {
          this.#releaseAddLockWhenNotificationOpens(allAddToCartContainers);
        } else {
          this.#releaseAddLock(allAddToCartContainers);
        }

        if (addFailed) {
          window.__bhCartUpsell?.unlockCheckout?.('atc');
        } else {
          // Unlock after cart:update + discount sync; safety unlock if event never fires.
          window.setTimeout(() => window.__bhCartUpsell?.unlockCheckout?.('atc'), 8000);
        }

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;
    if (this.#isAddingToCart || ProductFormComponent.#globalAddLock) return;

    this.#isAddingToCart = true;
    ProductFormComponent.#globalAddLock = true;
    ProductFormComponent.#installCartAddDedupe();
    window.__bhCartUpsell?.lockCheckout?.('atc');

    const { addToCartTextError } = this.refs;
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const sectionIds = [...cartItemComponentsSectionIds];
    if (this.shouldRequestCartNotification) {
      sectionIds.push('dcoded-llp-cart-notification-product');
    }

    const compareMeta = this.#kachingCompareMeta();
    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        ...(compareMeta.cents > 0
          ? {
              properties: {
                _kaching_compare: String(compareMeta.cents),
                _kaching_compare_qty: String(compareMeta.qty),
              },
            }
          : {}),
      })),
      sections: [...new Set(sectionIds.filter(Boolean))].join(','),
    };

    window.__dcodedLlpExpectThemeAdd = Date.now();
    const atcToken =
      typeof window.__dcodedLlpIssueAtcToken === 'function' ? window.__dcodedLlpIssueAtcToken() : '';

    if (!atcToken) {
      this.#releaseAddLock(allAddToCartContainers);
      return;
    }

    if (this.shouldRequestCartNotification) {
      document.dispatchEvent(
        new CustomEvent('dcoded-llp:atc-start', {
          bubbles: true,
          detail: this.#getNotificationSnapshot(),
        })
      );
    }

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Dcoded-Llp-Atc': '1',
        'X-Dcoded-Llp-Atc-Token': atcToken,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        if (this.shouldSubmitCheckout) {
          this.#submitCheckout();
          return;
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const addedItem = Array.isArray(response.items) ? response.items[0] : response;

        await this.#persistKachingCompareOnItem(addedItem);

        this.dispatchEvent(
          new CartAddEvent(addedItem || {}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            cartItemKey: addedItem?.key || response.key,
            redirectToCheckout: false,
            openCartDrawer: this.shouldOpenCartDrawer,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
        window.__bhCartUpsell?.unlockCheckout?.('atc');
      })
      .finally(() => {
        this.#releaseAddLock(allAddToCartContainers);
        window.setTimeout(() => window.__bhCartUpsell?.unlockCheckout?.('atc'), 8000);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #submitCheckout() {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = Theme.routes.cart_url;
    form.classList.add('visually-hidden');

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'checkout';

    form.appendChild(checkoutInput);
    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    // Don't run a queued add if a cart add just completed / is cooling down
    if (this.#addToCartQueue.length > 0 && !window.__dcodedLlpAtcBusy) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    } else {
      this.#addToCartQueue = [];
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
